import { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import OccupationLink from '@/components/shared/OccupationLink';
import { toTitleCase } from '@/lib/utils';
import { useComparisonLimits } from '@/hooks/useComparisonLimits';

interface OccupationOption {
  code: string;
  title: string;
}

interface MultiOccupationSelectorProps {
  selectedOccupations: OccupationOption[];
  onSelectionChange: (occupations: OccupationOption[]) => void;
  primaryOccupation?: OccupationOption;
  className?: string;
}

export default function MultiOccupationSelector({
  selectedOccupations,
  onSelectionChange,
  primaryOccupation,
  className = ""
}: MultiOccupationSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { canAddMore, incrementComparisons } = useComparisonLimits();

  // Multi-occupation comparison is free for everyone (up to 5 at a time).
  const maxOccupations = 5;

  // Fetch available occupations using api-occupations function
  const { data: occupations = [], isLoading: isSearching, error: searchError } = useQuery({
    queryKey: ['occupations-search', searchQuery],
    queryFn: async () => {
      console.log('[MultiOccupationSelector] Query function called', { 
        searchQuery, 
        searchQueryLength: searchQuery.length,
        searchQueryTrimmed: searchQuery.trim() 
      });
      
      if (!searchQuery.trim()) {
        console.log('[MultiOccupationSelector] Empty search query, returning empty array');
        return [];
      }
      
      console.log('[MultiOccupationSelector] Invoking api-occupations', { 
        search: searchQuery,
        limit: 50 
      });
      
      try {
        const { data, error } = await supabase.functions.invoke('api-occupations', {
          body: { 
            search: searchQuery,
            limit: 50
          }
        });
        
        console.log('[MultiOccupationSelector] API response received', { 
          hasData: !!data,
          hasError: !!error,
          dataKeys: data ? Object.keys(data) : [],
          errorDetails: error
        });
        
        if (error) {
          console.error('[MultiOccupationSelector] Error fetching occupations:', {
            error,
            errorMessage: error.message,
            errorDetails: JSON.stringify(error, null, 2)
          });
          return [];
        }
        
        if (!data) {
          console.warn('[MultiOccupationSelector] No data in response');
          return [];
        }
        
        console.log('[MultiOccupationSelector] Processing occupations', {
          occupationsCount: data?.occupations?.length || 0,
          firstOccupation: data?.occupations?.[0],
          dataStructure: data
        });
        
        // Map the response from api-occupations to the expected format
        // Deduplicate by soc_code since there may be multiple entries per occupation
        const uniqueOccupations = (data?.occupations || []).reduce((acc: unknown[], item: unknown) => {
          if (!item) {
            console.warn('[MultiOccupationSelector] Skipping null/undefined item');
            return acc;
          }
          
          if (!item.soc_code) {
            console.warn('[MultiOccupationSelector] Item missing soc_code', item);
            return acc;
          }
          
          if (!item.occ_title) {
            console.warn('[MultiOccupationSelector] Item missing occ_title', item);
            return acc;
          }
          
          if (!acc.some(existing => existing.code === item.soc_code)) {
            acc.push({
              code: item.soc_code,
              title: toTitleCase(item.occ_title)
            });
          }
          return acc;
        }, []);

        console.log('[MultiOccupationSelector] Mapped occupations', {
          uniqueCount: uniqueOccupations.length,
          sample: uniqueOccupations.slice(0, 3)
        });

        return uniqueOccupations;
      } catch (err) {
        console.error('[MultiOccupationSelector] Exception in query function:', {
          error: err,
          errorMessage: err instanceof Error ? err.message : String(err),
          errorStack: err instanceof Error ? err.stack : undefined
        });
        return [];
      }
    },
    enabled: searchQuery.length > 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
  
  // Debug query state
  useEffect(() => {
    console.log('[MultiOccupationSelector] Query state', {
      searchQuery,
      searchQueryLength: searchQuery.length,
      isEnabled: searchQuery.length > 2,
      isLoading: isSearching,
      hasError: !!searchError,
      occupationsCount: occupations.length,
      error: searchError
    });
  }, [searchQuery, isSearching, searchError, occupations.length]);

  const handleSelect = (occupation: OccupationOption) => {
    const isSelected = selectedOccupations.some(occ => occ.code === occupation.code);
    
    if (isSelected) {
      onSelectionChange(selectedOccupations.filter(occ => occ.code !== occupation.code));
    } else {
      if (selectedOccupations.length >= maxOccupations || !canAddMore) {
        return; // Prevent adding more than allowed
      }
      onSelectionChange([...selectedOccupations, occupation]);
      incrementComparisons();
    }
  };

  const removeOccupation = (occupationCode: string) => {
    onSelectionChange(selectedOccupations.filter(occ => occ.code !== occupationCode));
  };

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Compare Occupations</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Primary occupation display */}
        {primaryOccupation && (
          <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-center justify-between">
              <div>
                <Badge variant="secondary" className="mb-1">Primary</Badge>
                <div className="font-medium text-sm">
                  <OccupationLink 
                    occupation={{ 
                      occ_title: primaryOccupation.title,
                      soc: primaryOccupation.code 
                    }}
                    className="hover:text-primary/80"
                  >
                    {primaryOccupation.title}
                  </OccupationLink>
                </div>
                <div className="text-xs text-muted-foreground">{primaryOccupation.code}</div>
              </div>
            </div>
          </div>
        )}

        {/* Selected occupations */}
        {selectedOccupations.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Selected for Comparison ({selectedOccupations.length}/{maxOccupations})</div>
            {selectedOccupations.map((occupation) => (
              <div key={occupation.code} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">
                    <OccupationLink 
                      occupation={{ 
                        occ_title: occupation.title,
                        soc: occupation.code 
                      }}
                      className="hover:text-primary/80"
                    >
                      {occupation.title}
                    </OccupationLink>
                  </div>
                  <div className="text-xs text-muted-foreground">{occupation.code}</div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeOccupation(occupation.code)}
                  className="h-6 w-6 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Occupation selector */}
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start"
              disabled={selectedOccupations.length >= maxOccupations}
            >
              {selectedOccupations.length >= maxOccupations
                ? `Maximum ${maxOccupations} occupations selected`
                : 'Add occupation to compare...'
              }
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search occupations..."
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList>
                <CommandEmpty>
                  {searchQuery.length < 3 
                    ? "Type at least 3 characters to search" 
                    : "No occupations found"
                  }
                </CommandEmpty>
                {occupations.length > 0 && (
                  <CommandGroup>
                    {occupations.map((occupation) => {
                      const isSelected = selectedOccupations.some(occ => occ.code === occupation.code);
                      const isPrimary = primaryOccupation?.code === occupation.code;
                      
                      if (isPrimary) return null; // Don't show primary occupation in comparison list
                      
                      return (
                        <CommandItem
                          key={occupation.code}
                          onSelect={() => handleSelect(occupation)}
                          className="flex items-center justify-between"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{occupation.title}</div>
                            <div className="text-xs text-muted-foreground">{occupation.code}</div>
                          </div>
                          {isSelected && <Check className="h-4 w-4 text-primary" />}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </CardContent>
    </Card>
  );
}